import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    Briefcase,
    Check,
    Clock3,
    Home,
    Locate,
    MapPin,
    Pencil,
    Plus,
    Tag,
    Trash2,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SubmitButton } from "@/components/form/SubmitButton";
import { useCart } from "@/features/cart/CartContext";
import { useAuth } from "@/features/auth/AuthContext";
import { payWithRazorpay } from "@/features/payments/razorpay";
import {
    addressApi,
    orderApi,
    type Address,
    type AddressUpsert,
    type PriceBreakdown,
} from "@/features/commerce/api";
import { formatApiError } from "@/features/auth/formatError";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/lib/utils";

function fINR(n: number) {
    return `₹${n.toLocaleString("en-IN")}`;
}

type DeliverySlot = { value: string; label: string; sub: string };
const SLOTS: DeliverySlot[] = [
    { value: "instant", label: "Instant", sub: "Delivered in ~12 min" },
    { value: "scheduled_morning", label: "Morning slot", sub: "8 – 11 AM tomorrow" },
    { value: "scheduled_evening", label: "Evening slot", sub: "5 – 8 PM tomorrow" },
];

type PaymentMethod = { value: string; label: string; sub: string };
const PAYMENTS: PaymentMethod[] = [
    { value: "cod", label: "Cash on delivery", sub: "Pay when it arrives" },
    { value: "wallet", label: "TSGo Wallet", sub: "Pay from your wallet balance" },
    { value: "razorpay", label: "Pay Online", sub: "Pay securely using Razorpay · UPI, cards, net banking" },
];

const LABEL_ICONS: Record<Address["label"], React.ComponentType<{ className?: string }>> = {
    home: Home,
    work: Briefcase,
    other: MapPin,
};

export default function Checkout() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { cart, refresh } = useCart();
    const { user } = useAuth();

    const addressesQuery = useQuery({
        queryKey: ["addresses"],
        queryFn: addressApi.list,
    });

    const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState<Address | null>(null);

    const [slot, setSlot] = useState<string>("instant");
    const [payment, setPayment] = useState<string>("cod");
    const [couponInput, setCouponInput] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
    const [couponError, setCouponError] = useState<string | null>(null);

    const [breakdown, setBreakdown] = useState<PriceBreakdown | null>(null);
    const [quoting, setQuoting] = useState(false);
    const [placing, setPlacing] = useState(false);

    useEffect(() => {
        if (!selectedAddressId && addressesQuery.data && addressesQuery.data.length > 0) {
            const d = addressesQuery.data.find((a) => a.is_default) ?? addressesQuery.data[0];
            setSelectedAddressId(d.id);
        }
    }, [addressesQuery.data, selectedAddressId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (cart.items.length === 0) {
                setBreakdown(null);
                return;
            }
            setQuoting(true);
            try {
                const b = await orderApi.quote(appliedCoupon);
                if (!cancelled) setBreakdown(b);
            } catch (err) {
                if (!cancelled) toast.error(formatApiError(err));
            } finally {
                if (!cancelled) setQuoting(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [cart.items.length, cart.subtotal, appliedCoupon]);

    if (cart.items.length === 0) {
        return (
            <div className="tsgo-page tsgo-container mx-auto max-w-lg py-16 text-center">
                <h1 className="tsgo-h1">Your cart is empty</h1>
                <p className="tsgo-lead mt-2">Add items before checking out.</p>
                <Button
                    className="mt-6 bg-brand text-white hover:bg-brand-600"
                    onClick={() => navigate(ROUTES.home)}
                >
                    Start shopping
                </Button>
            </div>
        );
    }

    const applyCoupon = async () => {
        setCouponError(null);
        const code = couponInput.trim().toUpperCase();
        if (!code) {
            setAppliedCoupon(null);
            return;
        }
        try {
            const res = await orderApi.validateCoupon(code);
            if (!res.valid) {
                setCouponError(res.error ?? "Invalid coupon");
                setAppliedCoupon(null);
                return;
            }
            setAppliedCoupon(code);
            toast.success(`Coupon ${code} applied`);
        } catch (err) {
            setCouponError(formatApiError(err));
        }
    };

    const clearCoupon = () => {
        setAppliedCoupon(null);
        setCouponInput("");
        setCouponError(null);
    };

    const placeOrder = async () => {
        if (!selectedAddressId) {
            toast.error("Please select a delivery address");
            return;
        }
        setPlacing(true);
        try {
            const order = await orderApi.place({
                address_id: selectedAddressId,
                payment_method: payment,
                delivery_slot: slot,
                coupon_code: appliedCoupon,
            });
            if (payment === "razorpay") {
                try {
                    const paid = await payWithRazorpay(order, {
                        name: user?.full_name,
                        email: user?.email,
                        contact: user?.phone || undefined,
                    });
                    await refresh();
                    qc.invalidateQueries({ queryKey: ["orders"] });
                    navigate(ROUTES.orderSuccessBuild(paid.id), { replace: true });
                } catch (err) {
                    qc.invalidateQueries({ queryKey: ["orders"] });
                    navigate(ROUTES.orderDetailBuild(order.id), { replace: true });
                    throw err;
                }
                return;
            }
            await refresh();
            qc.invalidateQueries({ queryKey: ["orders"] });
            navigate(ROUTES.orderSuccessBuild(order.id), { replace: true });
        } catch (err) {
            toast.error(formatApiError(err, "Could not place order"));
        } finally {
            setPlacing(false);
        }
    };

    return (
        <div data-testid="checkout-page" className="tsgo-page">
            <section className="tsgo-container pt-6 sm:pt-10">
                <h1 className="tsgo-h1">Checkout</h1>
                <p className="tsgo-lead mt-2">
                    Confirm your delivery, choose a slot, and pay.
                </p>
            </section>

            <section className="tsgo-container mt-8 grid gap-6 lg:grid-cols-[1fr_380px]">
                <div className="space-y-6">
                    {/* Addresses */}
                    <Card title="Delivery address" testId="checkout-addresses">
                        {addressesQuery.isLoading ? (
                            <div className="h-20 animate-pulse rounded-xl bg-muted" />
                        ) : (addressesQuery.data ?? []).length === 0 ? (
                            <div className="rounded-xl border border-dashed border-border p-6 text-center">
                                <p className="text-sm text-muted-foreground">
                                    No saved addresses yet.
                                </p>
                                <Button
                                    className="mt-3 bg-brand text-white hover:bg-brand-600"
                                    onClick={() => {
                                        setEditing(null);
                                        setShowForm(true);
                                    }}
                                    data-testid="checkout-add-address-empty"
                                >
                                    <Plus className="mr-1 h-4 w-4" /> Add an address
                                </Button>
                            </div>
                        ) : (
                            <>
                                <ul className="grid gap-3 sm:grid-cols-2">
                                    {(addressesQuery.data ?? []).map((a) => {
                                        const Icon = LABEL_ICONS[a.label] ?? MapPin;
                                        const selected = selectedAddressId === a.id;
                                        return (
                                            <li key={a.id}>
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => setSelectedAddressId(a.id)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            setSelectedAddressId(a.id);
                                                        }
                                                    }}
                                                    data-testid={`address-${a.id}`}
                                                    aria-pressed={selected}
                                                    className={cn(
                                                        "flex w-full cursor-pointer items-start gap-3 rounded-xl border p-3 text-left transition",
                                                        selected
                                                            ? "border-brand bg-brand/5 ring-2 ring-brand/20"
                                                            : "border-border/70 bg-background hover:border-brand/40",
                                                    )}
                                                >
                                                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand/10 text-brand">
                                                        <Icon className="h-4 w-4" />
                                                    </span>
                                                    <div className="flex-1 text-sm">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold capitalize">{a.label}</span>
                                                            {a.is_default ? (
                                                                <span className="rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-brand">
                                                                    Default
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        <p className="mt-0.5 font-semibold">{a.full_name}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {a.line1}
                                                            {a.line2 ? `, ${a.line2}` : ""},{" "}
                                                            {a.city} — {a.pincode}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {a.phone}
                                                        </p>
                                                        <div className="mt-2 flex gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditing(a);
                                                                    setShowForm(true);
                                                                }}
                                                                className="text-[11px] font-semibold text-foreground/70 hover:text-brand"
                                                                data-testid={`address-edit-${a.id}`}
                                                            >
                                                                <Pencil className="mr-1 inline h-3 w-3" /> Edit
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    try {
                                                                        await addressApi.remove(a.id);
                                                                        qc.invalidateQueries({ queryKey: ["addresses"] });
                                                                        if (selectedAddressId === a.id) setSelectedAddressId(null);
                                                                        toast("Address removed");
                                                                    } catch (err) {
                                                                        toast.error(formatApiError(err));
                                                                    }
                                                                }}
                                                                className="text-[11px] font-semibold text-error hover:underline"
                                                                data-testid={`address-delete-${a.id}`}
                                                            >
                                                                <Trash2 className="mr-1 inline h-3 w-3" /> Delete
                                                            </button>
                                                            {!a.is_default ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        try {
                                                                            await addressApi.setDefault(a.id);
                                                                            qc.invalidateQueries({ queryKey: ["addresses"] });
                                                                        } catch (err) {
                                                                            toast.error(formatApiError(err));
                                                                        }
                                                                    }}
                                                                    className="text-[11px] font-semibold text-brand hover:underline"
                                                                >
                                                                    Set default
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                    {selected ? (
                                                        <span className="grid h-6 w-6 place-items-center rounded-full bg-brand text-white">
                                                            <Check className="h-3 w-3" />
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <Button
                                    variant="outline"
                                    className="mt-4"
                                    onClick={() => {
                                        setEditing(null);
                                        setShowForm(true);
                                    }}
                                    data-testid="checkout-add-address"
                                >
                                    <Plus className="mr-1 h-4 w-4" /> Add new address
                                </Button>
                            </>
                        )}
                    </Card>

                    {/* Delivery slot */}
                    <Card title="Delivery slot" testId="checkout-slots">
                        <ul className="grid gap-3 sm:grid-cols-3">
                            {SLOTS.map((s) => (
                                <li key={s.value}>
                                    <button
                                        type="button"
                                        onClick={() => setSlot(s.value)}
                                        data-testid={`slot-${s.value}`}
                                        className={cn(
                                            "flex w-full flex-col items-start gap-1 rounded-xl border p-3 text-left transition",
                                            slot === s.value
                                                ? "border-brand bg-brand/5 ring-2 ring-brand/20"
                                                : "border-border/70 hover:border-brand/40",
                                        )}
                                    >
                                        <span className="inline-flex items-center gap-1 text-sm font-semibold">
                                            <Clock3 className="h-4 w-4 text-brand" /> {s.label}
                                        </span>
                                        <span className="text-xs text-muted-foreground">{s.sub}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </Card>

                    {/* Payment */}
                    <Card title="Payment method" testId="checkout-payment">
                        <ul className="grid gap-2 sm:grid-cols-3" aria-label="Choose a payment method">
                            {PAYMENTS.map((p) => (
                                <li key={p.value}>
                                    <button
                                        type="button"
                                        onClick={() => setPayment(p.value)}
                                        data-testid={`payment-${p.value}`}
                                        aria-pressed={payment === p.value}
                                        className={cn(
                                            "flex w-full items-start gap-2 rounded-xl border p-3 text-left transition",
                                            payment === p.value
                                                ? "border-brand bg-brand/5 ring-2 ring-brand/20"
                                                : "border-border/70 hover:border-brand/40",
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                "mt-0.5 grid h-4 w-4 place-items-center rounded-full border",
                                                payment === p.value ? "border-brand" : "border-border",
                                            )}
                                        >
                                            {payment === p.value ? <span className="h-2 w-2 rounded-full bg-brand" /> : null}
                                        </span>
                                        <div>
                                            <p className="text-sm font-semibold">{p.label}</p>
                                            <p className="text-xs text-muted-foreground">{p.sub}</p>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </Card>
                </div>

                {/* Summary column */}
                <aside className="h-fit space-y-4">
                    <Card title="Have a coupon?" testId="checkout-coupon">
                        {appliedCoupon ? (
                            <div className="flex items-center justify-between rounded-xl border border-brand/40 bg-brand/5 p-3 text-sm">
                                <span className="inline-flex items-center gap-2 font-semibold text-brand">
                                    <Tag className="h-4 w-4" /> {appliedCoupon} applied
                                </span>
                                <button
                                    type="button"
                                    onClick={clearCoupon}
                                    className="text-xs font-semibold text-error hover:underline"
                                    data-testid="coupon-clear"
                                >
                                    <X className="mr-1 inline h-3 w-3" /> Remove
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Try TSGO100"
                                        value={couponInput}
                                        onChange={(e) => setCouponInput(e.target.value)}
                                        className="h-10"
                                        data-testid="coupon-input"
                                    />
                                    <Button
                                        onClick={applyCoupon}
                                        className="bg-ink text-white hover:bg-ink/90"
                                        data-testid="coupon-apply"
                                    >
                                        Apply
                                    </Button>
                                </div>
                                {couponError ? (
                                    <p className="text-xs font-medium text-error">{couponError}</p>
                                ) : (
                                    <p className="text-[11px] text-muted-foreground">
                                        Try <b>TSGO100</b>, <b>WELCOME50</b> or <b>FREESHIP</b>.
                                    </p>
                                )}
                            </div>
                        )}
                    </Card>

                    <Card title="Order summary" testId="checkout-summary">
                        {quoting || !breakdown ? (
                            <div className="h-24 animate-pulse rounded-xl bg-muted" />
                        ) : (
                            <>
                                <dl className="space-y-2 text-sm">
                                    <Row label="Item total" value={fINR(breakdown.subtotal)} />
                                    <Row label="Delivery" value={breakdown.delivery_charge === 0 ? "FREE" : fINR(breakdown.delivery_charge)} strong={breakdown.delivery_charge === 0} />
                                    <Row label="Platform fee" value={fINR(breakdown.platform_fee)} />
                                    <Row label="Handling fee" value={fINR(breakdown.handling_fee)} />
                                    <Row label="GST (5%)" value={fINR(breakdown.gst)} />
                                    {breakdown.coupon_discount > 0 ? (
                                        <Row label={`Coupon (${breakdown.coupon_code})`} value={`- ${fINR(breakdown.coupon_discount)}`} tone="brand" />
                                    ) : null}
                                    <div className="my-2 border-t border-border/70" />
                                    <div className="flex items-center justify-between text-base">
                                        <dt className="font-bold">Grand total</dt>
                                        <dd className="text-lg font-extrabold" data-testid="checkout-total">{fINR(breakdown.total)}</dd>
                                    </div>
                                    {breakdown.total_savings > 0 ? (
                                        <div className="rounded-lg bg-brand/10 px-2 py-1 text-center text-[11px] font-bold text-brand">
                                            You save {fINR(breakdown.total_savings)} on this order
                                        </div>
                                    ) : null}
                                </dl>
                                <SubmitButton
                                    type="button"
                                    loading={placing}
                                    loadingText="Placing order…"
                                    onClick={placeOrder}
                                    className="mt-5"
                                    data-testid="checkout-place-order"
                                    disabled={!selectedAddressId}
                                >
                                    {payment === "razorpay" ? "Pay online" : "Place order"} · {fINR(breakdown.total)}
                                </SubmitButton>
                            </>
                        )}
                    </Card>
                </aside>
            </section>

            {showForm ? (
                <AddressFormModal
                    address={editing ?? undefined}
                    onClose={() => {
                        setShowForm(false);
                        setEditing(null);
                    }}
                    onSaved={(a) => {
                        setShowForm(false);
                        setEditing(null);
                        setSelectedAddressId(a.id);
                        qc.invalidateQueries({ queryKey: ["addresses"] });
                    }}
                />
            ) : null}
        </div>
    );
}

function Card({
    title,
    children,
    testId,
}: {
    title: string;
    children: React.ReactNode;
    testId?: string;
}) {
    return (
        <div className="rounded-2xl border border-border/70 bg-card p-4 shadow-soft sm:p-6" data-testid={testId}>
            <h3 className="tsgo-h3">{title}</h3>
            <div className="mt-4">{children}</div>
        </div>
    );
}

function Row({
    label,
    value,
    tone = "default",
    strong,
}: {
    label: string;
    value: string;
    tone?: "default" | "brand";
    strong?: boolean;
}) {
    return (
        <div className="flex items-center justify-between">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className={cn("font-semibold", tone === "brand" && "text-brand", strong && "text-brand")}>{value}</dd>
        </div>
    );
}

function AddressFormModal({
    address,
    onClose,
    onSaved,
}: {
    address?: Address;
    onClose: () => void;
    onSaved: (a: Address) => void;
}) {
    const [form, setForm] = useState<AddressUpsert>({
        label: address?.label ?? "home",
        full_name: address?.full_name ?? "",
        phone: address?.phone ?? "",
        line1: address?.line1 ?? "",
        line2: address?.line2 ?? "",
        city: address?.city ?? "",
        state: address?.state ?? "",
        pincode: address?.pincode ?? "",
        lat: address?.lat ?? null,
        lng: address?.lng ?? null,
        landmark: address?.landmark ?? "",
        is_default: address?.is_default ?? false,
    });
    const [saving, setSaving] = useState(false);
    const [locating, setLocating] = useState(false);

    const set = <K extends keyof AddressUpsert>(k: K, v: AddressUpsert[K]) =>
        setForm((f) => ({ ...f, [k]: v }));

    const locate = () => {
        if (!("geolocation" in navigator)) {
            toast.error("Location not supported on this device — enter address manually");
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                set("lat", lat);
                set("lng", lng);

                // Reverse-geocode when Google Maps is configured, else keep GPS only.
                try {
                    const { reverseGeocode, isGoogleMapsConfigured } = await import(
                        "@/features/maps/google"
                    );
                    if (isGoogleMapsConfigured()) {
                        const place = await reverseGeocode(lat, lng);
                        if (place) {
                            if (place.line1) set("line1", place.line1);
                            if (place.line2) set("line2", place.line2);
                            if (place.city) set("city", place.city);
                            if (place.state) set("state", place.state);
                            if (place.pincode) set("pincode", place.pincode);
                        }
                    }
                } catch {
                    /* geocoding is best-effort */
                }

                // Nearest store hint — helps customers understand delivery scope.
                try {
                    const { opsApi } = await import("@/features/operations/api");
                    const store = await opsApi.nearestStore(lat, lng);
                    if (store) {
                        toast.success(
                            `Location detected · nearest store: ${store.name}${
                                store.distance_km ? ` (${store.distance_km} km)` : ""
                            }`,
                        );
                    } else {
                        toast.success("Location detected");
                    }
                } catch {
                    toast.success("Location detected");
                }

                setLocating(false);
            },
            (err) => {
                setLocating(false);
                const msg =
                    err.code === err.PERMISSION_DENIED
                        ? "Location permission denied — you can still enter the address manually."
                        : err.code === err.POSITION_UNAVAILABLE
                          ? "Couldn't determine your location. Please check GPS/network and try again, or enter the address manually."
                          : err.code === err.TIMEOUT
                            ? "Location request timed out. Try again or enter the address manually."
                            : "Couldn't get your location — enter the address manually.";
                toast.error(msg);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
        );
    };

    const submit = async () => {
        setSaving(true);
        try {
            const saved = address
                ? await addressApi.update(address.id, form)
                : await addressApi.create(form);
            toast.success(address ? "Address updated" : "Address added");
            onSaved(saved);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 backdrop-blur-sm" onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                data-testid="address-form"
                className="max-h-[92vh] w-full max-w-lg overflow-auto rounded-2xl bg-background p-5 shadow-elevated"
            >
                <div className="flex items-start justify-between">
                    <h3 className="tsgo-h3">{address ? "Edit address" : "Add address"}</h3>
                    <button onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="mt-4 space-y-4">
                    <div className="flex gap-2" role="group" aria-label="Label">
                        {(["home", "work", "other"] as const).map((v) => {
                            const Icon = LABEL_ICONS[v];
                            return (
                                <button
                                    key={v}
                                    type="button"
                                    onClick={() => set("label", v)}
                                    data-testid={`address-label-${v}`}
                                    className={cn(
                                        "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold capitalize transition",
                                        form.label === v
                                            ? "border-brand bg-brand text-white"
                                            : "border-border bg-background hover:border-brand/40",
                                    )}
                                >
                                    <Icon className="h-3.5 w-3.5" /> {v}
                                </button>
                            );
                        })}
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <Label>Full name</Label>
                            <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="address-fullname" />
                        </div>
                        <div>
                            <Label>Phone</Label>
                            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="address-phone" />
                        </div>
                    </div>
                    <div>
                        <Label>Address line 1</Label>
                        <Input value={form.line1} onChange={(e) => set("line1", e.target.value)} data-testid="address-line1" />
                    </div>
                    <div>
                        <Label>Address line 2 (optional)</Label>
                        <Input value={form.line2} onChange={(e) => set("line2", e.target.value)} />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                        <div>
                            <Label>City</Label>
                            <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="address-city" />
                        </div>
                        <div>
                            <Label>State</Label>
                            <Input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="address-state" />
                        </div>
                        <div>
                            <Label>Pincode</Label>
                            <Input value={form.pincode} onChange={(e) => set("pincode", e.target.value)} data-testid="address-pincode" />
                        </div>
                    </div>
                    <div>
                        <Label>Landmark (optional)</Label>
                        <Input value={form.landmark} onChange={(e) => set("landmark", e.target.value)} />
                    </div>

                    <div className="flex items-center justify-between rounded-xl border border-border/70 bg-muted/30 px-3 py-2">
                        <div className="flex items-center gap-2 text-xs">
                            <MapPin className="h-4 w-4 text-brand" />
                            {form.lat && form.lng ? (
                                <span>Lat {form.lat.toFixed(4)}, Lng {form.lng.toFixed(4)}</span>
                            ) : (
                                <span className="text-muted-foreground">GPS not set (optional)</span>
                            )}
                        </div>
                        <Button variant="outline" onClick={locate} data-testid="address-gps" disabled={locating}>
                            <Locate className={cn("mr-1 h-4 w-4", locating && "animate-spin")} />
                            Detect my location
                        </Button>
                    </div>

                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={form.is_default}
                            onChange={(e) => set("is_default", e.target.checked)}
                            className="h-4 w-4 accent-brand"
                        />
                        Set as default address
                    </label>

                    <div className="flex gap-3 pt-2">
                        <Button variant="outline" onClick={onClose} disabled={saving} className="flex-1">
                            Cancel
                        </Button>
                        <SubmitButton
                            onClick={submit}
                            loading={saving}
                            loadingText="Saving…"
                            className="flex-1"
                            data-testid="address-save"
                        >
                            {address ? "Save changes" : "Add address"}
                        </SubmitButton>
                    </div>
                </div>
            </div>
        </div>
    );
}
